import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// #134 Glassmorphism 玻璃态 UI 动画
export const GlassmorphismUI: React.FC<{
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
  cardColor?: string;
}> = ({
  title = 'GLASSMORPHISM',
  subtitle = 'Modern UI Design',
  backgroundColor = '#667eea',
  cardColor = 'rgba(255, 255, 255, 0.1)',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 卡片进入动画
  const cardOpacity = spring({ frame, fps, config: { damping: 20, stiffness: 100 } });
  const cardScale = spring({ frame, fps, config: { damping: 15, stiffness: 80 } });
  const cardY = interpolate(cardScale, [0, 1], [100, 0]);

  // 3 张卡片的延迟进入
  const delays = [0, 10, 20];
  const cards = [
    { title: 'Design', desc: 'Create beautiful interfaces', icon: '🎨' },
    { title: 'Develop', desc: 'Build modern applications', icon: '💻' },
    { title: 'Deploy', desc: 'Ship to production fast', icon: '🚀' },
  ];

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, ${backgroundColor} 0%, #764ba2 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 背景装饰圆圈 */}
      <div
        style={{
          position: 'absolute',
          width: width * 0.6,
          height: width * 0.6,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          top: '10%',
          right: '-10%',
          filter: 'blur(40px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: width * 0.5,
          height: width * 0.5,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          bottom: '-5%',
          left: '-5%',
          filter: 'blur(50px)',
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: height * 0.1,
          textAlign: 'center',
          opacity: cardOpacity,
          transform: `translateY(${cardY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: height * 0.06,
            fontWeight: '700',
            color: 'white',
            margin: 0,
            textShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: height * 0.032,
            color: 'rgba(255,255,255,0.8)',
            marginTop: height * 0.01,
          }}
        >
          {subtitle}
        </p>
      </div>

      {/* 玻璃态卡片组 */}
      <div
        style={{
          display: 'flex',
          gap: width * 0.03,
          marginTop: height * 0.15,
        }}
      >
        {cards.map((card, i) => {
          const delay = delays[i];
          const cardFrame = Math.max(0, frame - delay);
          const opacity = spring({ frame: cardFrame, fps, config: { damping: 20, stiffness: 100 } });
          const scale = spring({ frame: cardFrame, fps, config: { damping: 15, stiffness: 80 } });
          const y = interpolate(scale, [0, 1], [50, 0]);

          return (
            <div
              key={i}
              style={{
                width: width * 0.25,
                height: height * 0.45,
                background: cardColor,
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 20,
                padding: width * 0.025,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                opacity,
                transform: `translateY(${y}px) scale(${scale})`,
              }}
            >
              {/* 图标 */}
              <div
                style={{
                  fontSize: height * 0.08,
                  marginBottom: height * 0.03,
                }}
              >
                {card.icon}
              </div>

              {/* 标题 */}
              <h2
                style={{
                  fontSize: height * 0.035,
                  fontWeight: '600',
                  color: 'white',
                  margin: 0,
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                }}
              >
                {card.title}
              </h2>

              {/* 描述 */}
              <p
                style={{
                  fontSize: height * 0.03,
                  color: 'rgba(255,255,255,0.7)',
                  marginTop: height * 0.015,
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {card.desc}
              </p>

              {/* 按钮 */}
              <div
                style={{
                  marginTop: 'auto',
                  padding: `${height * 0.015}px ${width * 0.025}px`,
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: 10,
                  color: 'white',
                  fontSize: height * 0.023,
                  fontWeight: '500',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                Learn More
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// #135 Matrix 代码雨效果
export const MatrixCodeRain: React.FC<{
  text?: string;
  fontSize?: number;
  primaryColor?: string;
  backgroundColor?: string;
}> = ({
  text = 'MATRIX',
  fontSize = 16,
  primaryColor = '#00ff00',
  backgroundColor = '#000000',
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // 字符列数
  const columns = Math.floor(width / fontSize);
  const rows = Math.floor(height / fontSize);

  // 生成字符网格
  const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789';
  
  // 每列的下落速度和偏移量
  const columns_data = Array.from({ length: columns }, (_, i) => {
    const speed = 3 + (i % 5); // 不同速度
    const offset = random(`col-${i}`) * 100; // 随机起始偏移
    return { speed, offset };
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        overflow: 'hidden',
        fontFamily: 'monospace',
      }}
    >
      {/* Canvas 代码雨效果（使用 SVG 模拟） */}
      <svg width={width} height={height}>
        <defs>
          {/* 顶部到底部的透明度渐变 */}
          <linearGradient id="fadeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0" />
            <stop offset="50%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* 绘制每列字符 */}
        {columns_data.map((col, i) => {
          const x = i * fontSize;
          const yBase = ((frame * col.speed + col.offset) % (height + fontSize * 20)) - fontSize * 10;
          
          return Array.from({ length: 20 }, (_, j) => {
            const y = yBase - j * fontSize;
            const char = chars[Math.floor(random(`char-${i}-${j}-${Math.floor(frame/10)}`) * chars.length)];
            const opacity = 1 - (j / 20) * 0.8; // 头部亮，尾部暗

            return (
              <text
                key={`${i}-${j}`}
                x={x}
                y={y}
                fontSize={fontSize}
                fill={primaryColor}
                fillOpacity={opacity}
                fontFamily="monospace"
              >
                {char}
              </text>
            );
          });
        })}
      </svg>

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: height * 0.12,
            fontWeight: '900',
            color: primaryColor,
            margin: 0,
            textShadow: `0 0 20px ${primaryColor}, 0 0 40px ${primaryColor}`,
            fontFamily: 'monospace',
            letterSpacing: 10,
          }}
        >
          {text}
        </h1>
        <p
          style={{
            fontSize: height * 0.025,
            color: primaryColor,
            marginTop: height * 0.02,
            opacity: 0.7,
            fontFamily: 'monospace',
          }}
        >
          Follow the white rabbit
        </p>
      </div>

      {/* 扫描线效果 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0, 255, 0, 0.03) 2px,
            rgba(0, 255, 0, 0.03) 4px
          )`,
          pointerEvents: 'none',
        }}
      />
    </AbsoluteFill>
  );
};

// #136 Lottie 动画集成（示例占位符，实际需要 JSON 文件）
export const LottieAnimation: React.FC<{
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
}> = ({
  title = 'LOTTIE',
  subtitle = 'After Effects to Remotion',
  backgroundColor = '#1a1a2e',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 模拟 Lottie 动画效果（因为需要实际的 JSON 文件）
  const rotation = interpolate(frame, [0, 150], [0, 360 * 3]);
  const scale = spring({ frame, fps, config: { damping: 12, stiffness: 100 } });

  // 生成装饰性形状
  const shapes = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    const radius = width * 0.15;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const rotation_offset = i * 30;
    
    return { x, y, rotation_offset, delay: i * 5 };
  });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, ${backgroundColor} 0%, #16213e 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 中心动画组 */}
      <div
        style={{
          position: 'relative',
          width: width * 0.4,
          height: width * 0.4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* 外圈形状 */}
        {shapes.map((shape, i) => {
          const shapeFrame = Math.max(0, frame - shape.delay);
          const opacity = spring({ frame: shapeFrame, fps, config: { damping: 20, stiffness: 100 } });
          const scale = spring({ frame: shapeFrame, fps, config: { damping: 15, stiffness: 80 } });

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) translate(${shape.x}px, ${shape.y}px) rotate(${rotation + shape.rotation_offset}deg) scale(${scale})`,
                width: 30,
                height: 30,
                opacity,
              }}
            >
              {i % 2 === 0 ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: i % 4 === 0 ? '#4ecdc4' : '#ff6b6b',
                    borderRadius: '50%',
                    boxShadow: `0 0 20px ${i % 4 === 0 ? '#4ecdc4' : '#ff6b6b'}`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: i % 3 === 0 ? '#a855f7' : '#fbbf24',
                    transform: 'rotate(45deg)',
                    boxShadow: `0 0 20px ${i % 3 === 0 ? '#a855f7' : '#fbbf24'}`,
                  }}
                />
              )}
            </div>
          );
        })}

        {/* 中心 Logo 占位符 */}
        <div
          style={{
            width: width * 0.15,
            height: width * 0.15,
            background: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            transform: `scale(${scale})`,
          }}
        >
          <span style={{ fontSize: width * 0.05 }}>🎬</span>
        </div>
      </div>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          bottom: height * 0.2,
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: height * 0.08,
            fontWeight: '900',
            color: 'white',
            margin: 0,
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: height * 0.025,
            color: 'rgba(255,255,255,0.7)',
            marginTop: height * 0.015,
          }}
        >
          {subtitle}
        </p>
      </div>

      {/* 提示文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: height * 0.08,
          fontSize: height * 0.018,
          color: 'rgba(255,255,255,0.5)',
          textAlign: 'center',
        }}
      >
        Import After Effects animations as JSON
      </div>
    </AbsoluteFill>
  );
};
